import {ActivatedRouteSnapshot, NavigationEnd, Router} from "@angular/router";
import {BehaviorSubject, Observable, startWith} from "rxjs";
import {filter, map} from "rxjs/operators";
import {CookieService} from "ngx-cookie-service";
import {GlobalUtils} from "./global-utils";

export const SERVICE_URL = "service";

/** Models */
interface GestionMenuRoutingWrapperWVO {
  gestionMenuRoutingWVOList: GestionMenuRoutingWVO[];
}

interface GestionMenuRoutingWVO {
  id?: number;
  key?: string;
  titre?: string;
  icone?: string;
  url?: string;
  externalUrl?: string;
  parentKey?: string;
  order?: number;
  hidden?: boolean;
}

class NodeMenu {

  id: number;
  key: string;
  label: string;
  icon: string
  routerLink: string[];
  order: number;
  parentKey: string;
  class: string;
  externalUrl?: string;

  constructor(id, key, label, icon, routerLink, order, parentKey, className,externalUrl) {
    this.key = key;
    this.id = id;
    this.label = label;
    this.icon = icon;
    this.routerLink = routerLink;
    this.order = order;
    this.parentKey = parentKey;
    this.class = className;
    this.externalUrl = externalUrl;
  }
}

class ContainerMenu {

  key: string;
  label: string;
  icon: string
  items: NodeMenu[];
  order: number;
  class: string;

  constructor(key, label, icon, items, order, className) {
    this.key = key;
    this.label = label;
    this.icon = icon;
    this.items = items;
    this.order = order;
    this.class = className;
  }
}

interface Breadcrumb {
  label: string;
  url?: string;
  icon?: string;
  parent: string;
  parentLabel?: string;
}

/**
 * Utility class for managing menu and breadcrumbs.
 */
export class MenuUtil {

  /**
   * Observable holding the current list of breadcrumbs.
   *
   * @type {BehaviorSubject<Breadcrumb[]>}
   * @readonly
   * @static
   */
  public static readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

  /**
   * Observable holding the current menu ID.
   *
   * @type {BehaviorSubject<GestionMenuRoutingWVO | NodeMenu | null>}
   * @readonly
   * @static
   */
  public static readonly _menuIdSubject$ = new BehaviorSubject<GestionMenuRoutingWVO | NodeMenu | any>(null);

  /**
   * Observable for managing local storage state.
   *
   * @type {BehaviorSubject<any>}
   * @private
   * @static
   */
  private static localStorageSubject = new BehaviorSubject<any>(null);

  public static alertDsnUrl = 'alertes-dsn';
  public static demandeAdhesionUrl = 'suivi-demande-adhesion';
  public static suiviDemande = 'suivi-des-demandes';
  public static courriers = 'mes-courriers';

  // Mapping des routes hors menu pour le breadcrumb
  private static monProfil : { [key: string]: string } = {'mon-profil': 'Mon profil'};

  private static service: any;
  private static router: Router;
  private static cookieService: CookieService;

  private static get loadMenuRoutingWrapperFromLocalStorage() {
    return JSON.parse(GlobalUtils.getStorageItem("router"));
  }

  /**
   * Sets the Service instance.
   * @param {service} service - The Service instance to be set.
   * @static
   */
  public static setService(service: any) {
    this.service = service;
  }

  /**
   * Sets the CookieService instance.
   * @param {cookieService} cookieService - The CookieService instance to be set.
   * @static
   */
  public static setCookieService(cookieService: any) {
    this.cookieService = cookieService;
  }

  /**
   * Sets the Router instance.
   * @param {Router} service - The Router instance to be set.
   * @static
   */
  public static setRouter(service: Router) {
    this.router = service;
  }

  /**
   * Initializes breadcrumbs by subscribing to Router navigation events.
   * Updates the breadcrumb list whenever a navigation event occurs.
   * @static
   */
  public static initBreadcrumb() {
    this.constructBreadcrumb();
  }

  /**
   * Updates the breadcrumb trail based on the current route.
   *
   * This method initializes the breadcrumb list by loading menu routing data from local storage
   * and analyzing the current router state. It traverses the route snapshot tree to build a
   * corresponding breadcrumb structure, which is then emitted to update subscribers.
   *
   * @private
   * @returns {void}
   */
  private static constructBreadcrumb(){
    const menuRoutingWrapper: GestionMenuRoutingWVO[] = this.loadMenuRoutingWrapperFromLocalStorage;
    const root = this.router.routerState.snapshot.root;
    const breadcrumbs: Breadcrumb[] = [];
    this.addBreadcrumb(root, [], breadcrumbs, menuRoutingWrapper);
    this._breadcrumbs$.next(breadcrumbs);
  }

  /**
   * Subscribes to Angular Router events to detect navigation changes and update `localStorageSubject`.
   *
   * - Filters events to only include `NavigationEnd`, ensuring updates only after navigation completes.
   * - Uses `startWith(this.router.url)` to trigger the logic immediately with the current URL.
   * - Pushes `menuJson` into `localStorageSubject` on each navigation event.
   */
  private static subscribeToRouterEvents(menuJson: string) {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(this.router.url)).subscribe(() => {
      this.localStorageSubject.next(menuJson);
    });
  }

  /**
   * Processes the menu data by fetching it from the PageServiceService and
   * transforming it into a list of menu items.
   * Call this function in order to create the menu items to display
   * @returns {Observable<any[]>} An observable emitting the processed menu items array.
   * @static
   */
  public static processMenu(): Observable<any[]> {
    return this.service.getRouting().pipe(
      map((routing: GestionMenuRoutingWrapperWVO) => {
        let menuList = JSON.stringify(routing.gestionMenuRoutingWVOList);
        GlobalUtils.setStorageItem('router', menuList);
        this.subscribeToRouterEvents(menuList);
        const nodeList: NodeMenu[] | any = [];
        const containerList: ContainerMenu[] | any = [];
        // Create container objects from routing data and populate the containerList.
        routing?.gestionMenuRoutingWVOList.forEach(item => this.processContainerItem(item, containerList));
        // Process menu items and assign them to their corresponding containers in the containerList.
        routing?.gestionMenuRoutingWVOList.forEach(item => this.processMenuItem(item, nodeList, containerList));

        return [...nodeList, ...containerList].sort((a, b) => a.order > b.order ? 1 : -1).map(item => {
          if (!item.routerLink) {
            return {
              ...item,
              items: item.items?.map(sub => ({
                ...sub,
                command: () => {
                  this.onSubmenuClick(sub);
                }
              }))
            };
          }
          return {
            ...item,
            command: () => {
              this._menuIdSubject$.next(item);
              this.constructBreadcrumb();
            }
          };
        });
      })
    );
  }



  private static onSubmenuClick(sub: any){
    if (!GlobalUtils.isEmpty(sub.externalUrl)) {
      window.open(sub.externalUrl, '_blank');
    }
    this.constructBreadcrumb();
    this._menuIdSubject$.next(sub);
  }

  /**
   * Updates active state for all menu items based on current selection
   */
  public static updateMenuActiveState(menuItems: any[], currentId: number): any[] {
    return menuItems.map(item => {
      if (item.items) {
        // This is a container with sub-items
        const hasActiveChild = item.items.some((child: any) => child.id === currentId);
        return {
          ...item,
          active: hasActiveChild,
          items: item.items.map((child: any) => ({
            ...child,
            active: child.id === currentId
          }))
        };
      } else {
        // This is a regular menu item
        return {
          ...item,
          active: item.id === currentId
        };
      }
    });
  }

  /**
   * Initialize active menu based on current route
   */
  public static initializeActiveMenu() {
    if (this.router) {
      const currentUrl = this.router.url.split('?')[0]; // Remove query parameters
      const menuWrapper = this.loadMenuRoutingWrapperFromLocalStorage;
      if (menuWrapper) {
        // Find the menu item that matches the current route
        // Try exact match first, then partial match
        let activeRoute = menuWrapper.find(route => route.url && currentUrl === route.url);
        if (!activeRoute) {
          activeRoute = menuWrapper.find(route => route.url && currentUrl.includes(route.url));
        }
        if (activeRoute) {
          this._menuIdSubject$.next(activeRoute);
        } else {
          this._menuIdSubject$.next(null);
        }
      }
    }
  }

  public static fetchRouteByPath(path: string): GestionMenuRoutingWVO {
    return this.loadMenuRoutingWrapperFromLocalStorage?.find(route => route.url === path);
  }

  private static fetchRouteById(id: number): GestionMenuRoutingWVO {
    return this.loadMenuRoutingWrapperFromLocalStorage?.find(route => route.id === id);
  }

  /**
   * Processes a menu item and adds it to the appropriate node or container list.
   *
   * Items with an external URL should not have a routing URL to avoid triggering the routing mechanism,
   * allowing navigation to the external URI instead.
   */
  private static processMenuItem(item: any, nodeList: NodeMenu[], containerList: ContainerMenu[]): void {
    if (item.url || item.externalUrl) {
      const node = new NodeMenu(item.id, item.key, item.titre, item.icone, item.url, item.order, item.parentKey, item.contentHtml,item.externalUrl);
      if (node.parentKey) {
        const container = containerList.find(element => element.key === node.parentKey);
        if (container) {
          container.items.push(node);
        }
      } else {
        nodeList.push(node);
      }
    }
  }

  /**
   * Processes a container item and adds it to the container list.
   *
   * Items with an external URL are not considered containers.
   * Only items without a URL or external URL are added.
   */
  private static processContainerItem(item: any, containerList: ContainerMenu[]): void {
    if (!item?.url && !item.externalUrl) {
      const container = new ContainerMenu(item.key, item.titre, item.icone, [], item.order, null);
      containerList.push(container);
    }
  }

  /**
   * Determines the appropriate route based on the service's submenus.
   * The "/service" submenus do not have URLs, so we fetch them using a hidden ID stored in cookies.
   *
   * If the current route matches the SERVICE_URL, the hidden ID is retrieved from the cookies
   * and used to fetch the route. Otherwise, the route is fetched using the route's path.
   */
  private static fetchRoute(route: ActivatedRouteSnapshot): GestionMenuRoutingWVO {
    if (route.routeConfig.path === SERVICE_URL) {
      const id = this._menuIdSubject$.getValue() ? this._menuIdSubject$.getValue().id : 0;
      return id !== 0 ? this.fetchRouteById(id) : this.getDefaultServicePage();
    } else
      return this.fetchRouteByPath(route.routeConfig.path);
  }

  /**
   * Retrieves the default service page from the local storage menu routing wrapper.
   * The default service page is determined by the route that matches the SERVICE_URL
   * and has the lowest order value.
   *
   * @returns {GestionMenuRoutingWVO} The default service page with the lowest order value.
   */
  private static getDefaultServicePage(): GestionMenuRoutingWVO {
    const defaultServiceMenu = this.loadMenuRoutingWrapperFromLocalStorage?.filter(route => route.url === SERVICE_URL)?.reduce((minItem, currentItem) => {
      return currentItem.order < (minItem?.order ?? Infinity) ? currentItem : minItem;
    }, null);
    this._menuIdSubject$.next(defaultServiceMenu);
    return defaultServiceMenu;
  }

  private static addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[], menuRoutingWrapper: GestionMenuRoutingWVO[]) {
    const routeUrl = parentUrl.concat(route.url.map(url => url.path));
    if (route.firstChild) {
      this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs, menuRoutingWrapper);
    }
    if (route.routeConfig) {
      let menuRouting: GestionMenuRoutingWVO = this.fetchRoute(route);
      
      // Fallback : si la route n'existe pas dans le menu backend, utiliser route.data['breadcrumb'] ou le mapping
      if (!menuRouting && route.routeConfig.path) {
        const breadcrumbTitle = route.data?.['breadcrumb'] || this.monProfil[route.routeConfig.path];
        if (breadcrumbTitle) {
          menuRouting = {
            url: route.routeConfig.path,
            titre: breadcrumbTitle,
            key: route.routeConfig.path
          } as GestionMenuRoutingWVO;
        }
      }
      
      if (menuRouting) {
        const parent: GestionMenuRoutingWVO = menuRouting.parentKey ? this.fetchParentRouteByKey(menuRouting.parentKey, menuRoutingWrapper) : null;
        breadcrumbs.push({
          label: menuRouting.titre,
          url: `/#/${menuRouting.url}/`,
          parent: route.data['parent'],
          parentLabel: parent ? parent.titre : undefined
        });
      }
    }
  }

  private static fetchParentRouteByKey(key: string, menuRoutingWrapper: GestionMenuRoutingWVO[]): GestionMenuRoutingWVO {
    return menuRoutingWrapper.find(route => route.key === key);
  }

  static get localStorageChanges(): Observable<any> {
    return this.localStorageSubject.asObservable();
  }

}
